/*
 * Copyright (c) Nexign, JSC, 2019-2024.
 */
/**
 * Widget to show subscribers max costs
 *
 * @author Yakov Gushchin
 * @author Fedorov Maxim
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.0.0
 */

import { Fragment, memo, useEffect } from 'react';

import { Typography } from '@nexign/scc-ui-kit';
import classNames from 'classnames';
import { connect, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { makeSelectFeatureEnabled } from '@/main/pages/PageAuth/selectors/authSelectors';
import { Skeleton } from '@components/common';
import { FlexColumnStyled } from '@components/common/Block/Flexbox';
import { LayoutStyled } from '@components/common/Block/Layout';
import { pushGTMAnalytic } from '@modules/analytics/GoogleTagManager/GTMActions';
import { IGTMPushAnalyticPayload } from '@modules/analytics/GoogleTagManager/types/GTMActionTypes';
import initTranslationCommon from '@modules/internationalization/constants/common';
import initTranslationPageDashboard from '@modules/internationalization/constants/pages/dashboard/dashboard';
import { IPageDashboardTranslation } from '@modules/internationalization/types';
import ICommonTranslation from '@modules/internationalization/types/common';
import { makeSelectLocalStorageSelectedAccount } from '@modules/storage/storageSelectors';
import { IRootReducer } from '@reducers/rootReducer';

import { WidgetSkeletonParagraph } from '../../constants/widgetSkeletonContants';
import { WidgetDateRangeStyled } from '../../styled/WidgetDateRangeStyled';
import { WidgetSubtitleStyled, WidgetTitleStyled } from '../../styled/WidgetStyled';
import WidgetError from '../../WidgetError';
import { IWidgetSearchAccountsItem } from '../../WidgetSearchAccounts/types/widgetSearchAccountsTypes';
import { EWidgetStatus } from '../../widgetTypes';
import { widgetSubscriberMaxCostsConfiguration } from '../constants/widgetSubscriberMaxCostsConfiguration';
import { WidgetSubscriberMaxCostsStyled } from '../styled/widgetSubscriberMaxCostsStyled';
import { cleanListSubscriberMaxCosts, widgetSubscriberMaxCostRequest } from '../widgetSubscriberMaxCostsActions';
import {
    makeSelectorSubscriberMaxCostsGetSubscribers,
    makeSelectorSubscriberMaxCostsIsError,
    makeSelectorSubscriberMaxCostsIsLoading,
    makeSelectSubscriberMaxCostsStatus,
} from '../widgetSubscriberMaxCostsSelects';
import { ISubscribersMaxCost } from '../widgetSubscriberMaxCostsType';
import WidgetSubscriberMaxCostsEmpty from './WidgetSubscriberMaxCostsEmpty';
import WidgetSubscriberMaxCostsSuccessful from './WidgetSubscriberMaxCostsSuccessful';

interface IWidgetSubscriberMaxCostsProps {
    translation: IPageDashboardTranslation & ICommonTranslation;
}

export type TWidgetSubscriberMaxCosts = IWidgetSubscriberMaxCostsProps & IConnectedProps & IDispatchProps;

export const WidgetSubscriberMaxCosts = ({
    cleanListSubscribers,
    error,
    loading,
    loadSubscribers,
    pushGtmAnalytic,
    selectedAccount,
    status,
    subscribers,
    translation,
}: TWidgetSubscriberMaxCosts) => {
    const isPreliminaryDataFeatureEnabled = useSelector(
        makeSelectFeatureEnabled(FeatureNameEnum.EXPENSES_PRELIMINARY_DATA),
    );

    useEffect(() => {
        cleanListSubscribers();
        loadSubscribers();
    }, [cleanListSubscribers, loadSubscribers, selectedAccount.id]);

    return (
        <FlexColumnStyled>
            <WidgetSubscriberMaxCostsStyled
                className={classNames('widget__widget-max-costs', {
                    'widget__widget-max-costs__empty': subscribers.length <= 0,
                    'widget__widget-max-costs__error': !!error,
                })}
            >
                <LayoutStyled className="mb-4">
                    <WidgetTitleStyled>{translation.widgets.subscriberMaxCosts.title}</WidgetTitleStyled>
                    {status === 'success' && (
                        <WidgetSubtitleStyled>
                            <WidgetDateRangeStyled>
                                {translation.commonWidget.from.toUpperCase()}
                                &nbsp;
                                {widgetSubscriberMaxCostsConfiguration.firstDayInMonth}
                                &nbsp;
                                {translation.commonWidget.to}
                                &nbsp;
                                {widgetSubscriberMaxCostsConfiguration.lastDayInMonth}
                                &nbsp;
                                {translation.calendar.monthDeclension[
                                    widgetSubscriberMaxCostsConfiguration.currentMonth
                                ].toLocaleLowerCase()}
                            </WidgetDateRangeStyled>
                        </WidgetSubtitleStyled>
                    )}
                    {status === 'success' && isPreliminaryDataFeatureEnabled && (
                        <Typography>{translation.widgets.costs.preliminaryData}</Typography>
                    )}
                </LayoutStyled>

                <Skeleton count={4} isLoading={loading} render={() => WidgetSkeletonParagraph}>
                    <Fragment>
                        {
                            {
                                empty: <WidgetSubscriberMaxCostsEmpty translation={translation} />,
                                error: <WidgetError text={translation.widgets.subscriberMaxCosts.fail.text} />,
                                success: (
                                    <WidgetSubscriberMaxCostsSuccessful
                                        pushGtmAnalytic={pushGtmAnalytic}
                                        subscribers={subscribers}
                                        translation={translation}
                                    />
                                ),
                            }[status]
                        }
                    </Fragment>
                </Skeleton>
            </WidgetSubscriberMaxCostsStyled>
        </FlexColumnStyled>
    );
};

WidgetSubscriberMaxCosts.defaultProps = {
    translation: { ...initTranslationPageDashboard, ...initTranslationCommon },
};

interface IConnectedProps {
    error: string;
    loading: boolean;
    selectedAccount: IWidgetSearchAccountsItem;
    status: EWidgetStatus;
    subscribers: ISubscribersMaxCost[];
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    error: makeSelectorSubscriberMaxCostsIsError(),
    loading: makeSelectorSubscriberMaxCostsIsLoading(),
    selectedAccount: makeSelectLocalStorageSelectedAccount(),
    status: makeSelectSubscriberMaxCostsStatus(),
    subscribers: makeSelectorSubscriberMaxCostsGetSubscribers(),
});

interface IDispatchProps {
    cleanListSubscribers: () => void;
    loadSubscribers: () => void;
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    cleanListSubscribers: () => dispatch(cleanListSubscriberMaxCosts()),
    loadSubscribers: () => dispatch(widgetSubscriberMaxCostRequest()),
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => dispatch(pushGTMAnalytic(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(WidgetSubscriberMaxCosts));
